import { useMemo, useState } from 'react';
import { useVault } from '../lib/vault';
import { estimateBits } from '../lib/crypto';
import { REMEMBER_LABEL, type RememberChoice } from '../lib/keystore';
import { ErrorNote, useAction } from '../components/ui';

const MIN_LENGTH = 12;

function Wordmark(): JSX.Element {
  return (
    <img className="brand-logo" src="/logo.svg" alt="WACH" style={{ height: 28, marginBottom: 20 }} />
  );
}

/**
 * Wortmarke und Produktname in einer Zeile.
 *
 * Beim Entsperren weiss man laengst, wo man ist - eine eigene Ueberschrift
 * "WACH LOCKER entsperren" waere eine Zeile, die nichts hinzufuegt. Der Name
 * neben dem Logo genuegt, und die Schriftgroesse des Logos gibt ihm den Rang.
 */
function Lockup(): JSX.Element {
  return (
    <h1 className="gate-lockup">
      <img className="brand-logo" src="/logo.svg" alt="WACH" />
      <span aria-hidden="true">–</span>
      <span>LOCKER</span>
    </h1>
  );
}

function Strength({ value }: { value: string }): JSX.Element {
  const bits = estimateBits(value);
  const level = bits < 60 ? 'weak' : bits < 90 ? 'fair' : 'good';
  const filled = bits < 60 ? 1 : bits < 90 ? 2 : 3;
  return (
    <>
      <div className="strength" aria-hidden="true">
        {[0, 1, 2].map((i) => (
          <span key={i} className={i < filled ? `on-${level}` : ''} />
        ))}
      </div>
      <p className="small muted" style={{ margin: '6px 0 0' }}>
        {value.length === 0
          ? `Mindestens ${MIN_LENGTH} Zeichen. Drei bis vier zufällige Wörter sind besser als ein kurzes Kryptisches.`
          : `Geschätzte Stärke: ${bits} Bit${bits < 60 ? ' – bitte länger wählen.' : bits < 90 ? ' – geht so.' : ' – gut.'}`}
      </p>
    </>
  );
}

export function LoginScreen(): JSX.Element {
  const authError = useMemo(
    () => new URLSearchParams(window.location.search).get('authError'),
    [],
  );

  return (
    <div className="gate">
      <div className="gate-card">
        <Wordmark />
        <h1>WACH LOCKER</h1>
        <p>
          Verwaltung von Zugangsdaten und Seriennummern. Übersicht über Abonnements und laufende
          Kosten.
        </p>
        <ErrorNote error={authError} />
        <a className="btn btn-primary" href="/auth/login" style={{ display: 'block', textAlign: 'center', marginTop: 18, textDecoration: 'none' }}>
          Mit Google anmelden
        </a>
      </div>
    </div>
  );
}

export function SetupKeyScreen(): JSX.Element {
  const { setupKey, session, logout } = useVault();
  const [pass, setPass] = useState('');
  const [repeat, setRepeat] = useState('');
  const { busy, error, setError, run } = useAction();

  const tooShort = pass.length > 0 && pass.length < MIN_LENGTH;
  const mismatch = repeat.length > 0 && pass !== repeat;
  const ready = pass.length >= MIN_LENGTH && pass === repeat;

  return (
    <div className="gate">
      <div className="gate-card">
        <Wordmark />
        <h1>Master-Passphrase festlegen</h1>
        <p>
          Angemeldet als {session?.user.email}. Der Google-Login sagt nur, <em>wer</em> du bist.
          Zum Entschlüsseln brauchst du zusätzlich diese Passphrase.
        </p>

        <div className="note note-warn" style={{ marginTop: 16 }}>
          <p style={{ marginBottom: 8 }}>
            Diese Passphrase liegt nirgends gespeichert – auch nicht auf dem Server. Vergisst du sie,
            kann sie niemand wiederherstellen. Du kannst dann nur einen neuen Schlüssel erzeugen und
            dir deine Zugänge von einer Kollegin oder einem Kollegen neu freigeben lassen.
          </p>
          <p style={{ margin: 0 }}>
            <strong>Nicht im Browser speichern.</strong> Der Chrome-Passwortmanager hängt an
            demselben Google-Konto, mit dem du dich hier anmeldest – dort abgelegt wären beide
            Schlösser wieder eines. Merken oder in einen getrennten Passwortmanager.
          </p>
        </div>

        <form
          style={{ marginTop: 16 }}
          onSubmit={async (e) => {
            e.preventDefault();
            if (!ready) {
              setError('Bitte zwei gleiche Passphrasen mit ausreichender Länge eingeben.');
              return;
            }
            await run(() => setupKey(pass));
          }}
        >
          <ErrorNote error={error} />
          <div className="field">
            <label htmlFor="pass">Master-Passphrase</label>
            <input
              id="pass"
              type="password"
              autoComplete="off"
              data-1p-ignore="true"
              data-lpignore="true"
              data-bwignore="true"
              value={pass}
              onChange={(e) => setPass(e.target.value)}
              autoFocus
            />
            <Strength value={pass} />
          </div>
          <div className="field">
            <label htmlFor="repeat">Wiederholen</label>
            <input
              id="repeat"
              type="password"
              autoComplete="off"
              data-1p-ignore="true"
              data-lpignore="true"
              data-bwignore="true"
              value={repeat}
              onChange={(e) => setRepeat(e.target.value)}
            />
            {mismatch ? <p className="small" style={{ color: 'var(--danger)', margin: '6px 0 0' }}>Die beiden Eingaben stimmen nicht überein.</p> : null}
            {tooShort ? <p className="small" style={{ color: 'var(--danger)', margin: '6px 0 0' }}>Zu kurz.</p> : null}
          </div>
          <button className="btn-primary" type="submit" disabled={busy || !ready} style={{ width: '100%' }}>
            {busy ? 'Schlüssel wird erzeugt …' : 'Schlüssel erzeugen'}
          </button>
        </form>

        <button className="btn-quiet btn-sm" style={{ marginTop: 12 }} onClick={() => void logout()}>
          Abmelden
        </button>
      </div>
    </div>
  );
}

export function UnlockScreen(): JSX.Element {
  const { unlock, session, logout, resetKey, rememberChoice } = useVault();
  const [pass, setPass] = useState('');
  const [remember, setRemember] = useState<RememberChoice>(rememberChoice);
  const [showReset, setShowReset] = useState(false);
  const { busy, error, run } = useAction();

  return (
    <div className="gate">
      <div className="gate-card">
        <Lockup />
        <p>{session?.user.email}</p>

        <form
          style={{ marginTop: 18 }}
          onSubmit={async (e) => {
            e.preventDefault();
            await run(() => unlock(pass, remember));
          }}
        >
          <ErrorNote error={error} />
          <div className="field">
            <label htmlFor="unlock-pass">Master-Passphrase</label>
            <input
              id="unlock-pass"
              type="password"
              autoComplete="off"
              data-1p-ignore="true"
              data-lpignore="true"
              data-bwignore="true"
              value={pass}
              onChange={(e) => setPass(e.target.value)}
              autoFocus
            />
          </div>
          <div className="field">
            <label htmlFor="unlock-remember">Auf diesem Gerät entsperrt bleiben</label>
            <select
              id="unlock-remember"
              value={remember}
              onChange={(e) => setRemember(e.target.value as RememberChoice)}
            >
              {(Object.keys(REMEMBER_LABEL) as RememberChoice[]).map((k) => (
                <option key={k} value={k}>
                  {REMEMBER_LABEL[k]}
                </option>
              ))}
            </select>
            <p className="small muted" style={{ margin: '6px 0 0' }}>
              {remember === 'never'
                ? 'Jedes Neuladen verlangt die Passphrase erneut. Am sichersten.'
                : 'Bitte benutzt das Entsperren auf Dauer nur, wenn es euer eigener Rechner ist. Bitte denkt daran, dass hier vertrauliche Informationen stehen.'}
            </p>
          </div>

          <button className="btn-primary" type="submit" disabled={busy || !pass} style={{ width: '100%' }}>
            {busy ? 'Entsperren …' : 'Entsperren'}
          </button>
        </form>

        <div className="row" style={{ marginTop: 14 }}>
          <button className="btn-quiet btn-sm" onClick={() => setShowReset((v) => !v)}>
            Passphrase vergessen?
          </button>
          <span className="spacer" />
          <button className="btn-quiet btn-sm" onClick={() => void logout()}>
            Abmelden
          </button>
        </div>

        {showReset ? (
          <div className="note note-warn" style={{ marginTop: 12 }}>
            <p style={{ marginBottom: 8 }}>
              Ohne die Passphrase lassen sich deine Einträge nicht mehr entschlüsseln. Du kannst
              einen neuen Schlüssel erzeugen – dabei gehen alle deine bestehenden Freigaben verloren
              und ein Admin muss sie dir zurückgeben.
            </p>
            <button
              className="btn-danger btn-sm"
              disabled={busy}
              onClick={async () => {
                await run(async () => {
                  await resetKey();
                });
              }}
            >
              Schlüssel zurücksetzen
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
